import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { FingerprintService } from 'src/app/services/fingerprint.service';
import { FingerprintLocalApiService, IfingerPrint, SIDE } from 'src/app/services/fingerprint-local-api.service';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface IFingerprintRegisterDialogData {
  idcard?: string | null;
  ben_id: string | null;
  claim_request_id: string | null;
}

@Component({
  selector: 'app-fingerprint-register-dialog',
  templateUrl: './fingerprint-register-dialog.component.html',
  styleUrls: ['./fingerprint-register-dialog.component.scss']
})
export class FingerprintRegisterDialogComponent implements OnInit {
  isLoading: boolean = false;
  fingerprints: IfingerPrint[] = [];
  SIDE = SIDE;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IFingerprintRegisterDialogData,
    private _fingerprintService: FingerprintService,
    private _localApiService: FingerprintLocalApiService,
    private _translateService: TranslateService,
    private _dialog: MatDialogRef<FingerprintRegisterDialogComponent>,
    private _snackBar: MatSnackBar,
    private _cd: ChangeDetectorRef
  ) { }

  ngOnInit(): void { }

  async startScan() {
    this.isLoading = true;
    try {
      const response = await lastValueFrom(this._localApiService.enrollment());
      if (response && response.fingerprints) {
        this.fingerprints = response.fingerprints;
      }
      this._cd.markForCheck();
    } catch (error) {
      console.error("Local scan enrollment failed:", error);
      this._openSnackbar('fingerprint.verify_fail', true);
    } finally {
      this.isLoading = false;
    }
  }

  async save() {
    if (!this.fingerprints.length) {
      this._openSnackbar('fingerprint.not_found', true);
      return;
    }

    this.isLoading = true;
    try {
      const sortedFingers = [...this.fingerprints].sort((a, b) => a.finger_index - b.finger_index);

      for (let f of sortedFingers) {
        const formData = new FormData();
        formData.append('idcard', this.data.idcard || '');
        formData.append('ben_id', this.data.ben_id || '');
        formData.append('claim_request_id', this.data.claim_request_id || '');
        formData.append('side', f.side);
        formData.append('quality', f.quality);
        formData.append('finger_index', f.finger_index.toString());

        // Convert base64 to File object
        const templateFile = this.base64ToFile(f.finger_template, `template_${f.finger_index}.dat`, 'application/octet-stream');
        const featuresetFile = this.base64ToFile(f.finger_featureset, `featureset_${f.finger_index}.dat`, 'application/octet-stream');

        formData.append('finger_template', templateFile);
        formData.append('finger_featureset', featuresetFile);

        // Convert base64 photos to File objects
        if (f.finger_photos && f.finger_photos.length > 0) {
          for (let idx = 0; idx < f.finger_photos.length; idx++) {
            const photoBase64 = f.finger_photos[idx];
            if (photoBase64) {
              const photoFile = this.base64ToFile(photoBase64, `photo_${f.finger_index}_${idx}.png`, 'image/png');
              formData.append('file', photoFile);
            }
          }
        }

        await lastValueFrom(this._fingerprintService.saveFingerprintRecord(formData));
      }

      this._openSnackbar('general.msg_success', false);
      this._dialog.close(true);
    } catch (error) {
      console.error("Failed to save fingerprint records:", error);
      this._openSnackbar('general.msg_fail', true);
    } finally {
      this.isLoading = false;
    }
  }

  private base64ToFile(base64: string, filename: string, mimeType: string): File {
    let bstr = atob(base64.replace(/^data:.*?;base64,/, ''));
    let n = bstr.length;
    let u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mimeType });
  }

  private _openSnackbar(messageKey: string, error?: boolean) {
    this._translateService.get(messageKey).subscribe((translation: string) => {
      this._snackBar.open(translation, '', {
        duration: 3000,
        horizontalPosition: 'center',
        panelClass: error ? 'panel-error' : 'panel-success'
      });
    });
  }

  get leftFingers() {
    return this.fingerprints.filter(f => f.finger_index > 5);
  }

  get rightFingers() {
    return this.fingerprints.filter(f => f.finger_index <= 5);
  }
}
